import { useState } from "react";
import { NumField, Stat, Note, CalcButton, BrangusSwitch, useResultsFocus, fmt, num, val, str } from "./ToolUI";

const scenario = (f, adg) => {
  const gain = val(f.final) - val(f.initial);
  if (!(gain > 0) || !(adg > 0)) return { days: NaN, feed: NaN, conv: NaN, costKg: NaN, margin: NaN };
  const days = gain / adg;
  const feed = ((val(f.initial) + val(f.final)) / 2) * (val(f.intake) / 100) * days;
  const conv = feed / gain;
  return { days, feed, conv, costKg: conv * val(f.cost), margin: gain * val(f.price) - feed * val(f.cost) };
};

const compute = (f) => {
  const adgB = val(f.adgA) * (1 + num(f.pct) / 100);
  return { adgA: val(f.adgA), adgB, a: scenario(f, val(f.adgA)), b: scenario(f, adgB) };
};

const ROWS = [["Días a terminación", "days", 0], ["Alimento consumido (kg)", "feed", 0], ["Conversión (kg alimento / kg carne)", "conv", 1], ["Costo por kg producido (US$)", "costKg", 2], ["Margen por animal (US$)", "margin", 0]];

const Results = ({ r, brangus }) => (
  <div className={`grid ${brangus ? "grid-cols-3" : "grid-cols-2"} gap-px bg-[#8B2B22]/10 text-xs sm:text-sm`} data-testid="conv-results">
    {[["", "Rodeo actual", "Con Brangus"], ...ROWS.map(([l, k, d]) => [l, fmt(r.a[k], d), fmt(r.b[k], d)])].map((row, i) =>
      row.slice(0, brangus ? 3 : 2).map((c, j) => (
        <div key={`${i}-${j}`} className={`p-3 ${i === 0 ? "bg-[#1D3324] text-stone-100 font-semibold" : j === 2 ? "bg-[#F3EAD7] font-semibold text-[#8B2B22]" : "bg-white"} ${j ? "text-right" : "text-[#5C635E]"}`}>{c}</div>
      )))}
  </div>
);

export const ConversionTool = ({ params }) => {
  const [f, setF] = useState(() => ({
    initial: "200", final: "450", adgA: str(params.adg_base), intake: str(params.intake_pct), cost: str(params.feed_cost), price: str(params.price_kg),
    pct: str(Math.round((params.adg_brangus / params.adg_base - 1) * 1000) / 10),
  }));
  const [brangus, setBrangus] = useState(true);
  const r = compute(f);
  const { ref, flash, go } = useResultsFocus();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="grid gap-10 lg:grid-cols-12" data-testid="tool-conversion">
      <form onSubmit={go} className="lg:col-span-5 grid grid-cols-2 gap-4 content-start">
        <NumField id="conv-initial" label="Peso inicial" suffix="kg" value={f.initial} onChange={set("initial")} />
        <NumField id="conv-final" label="Peso meta de venta" suffix="kg" value={f.final} onChange={set("final")} />
        <NumField id="conv-adg-a" label="Ganancia diaria actual" suffix="kg/día" value={f.adgA} onChange={set("adgA")} />
        <NumField id="conv-intake" label="Consumo (% del peso vivo)" suffix="%" value={f.intake} onChange={set("intake")} />
        <NumField id="conv-cost" label="Costo del alimento" suffix="US$/kg" value={f.cost} onChange={set("cost")} />
        <NumField id="conv-price" label="Precio de venta" suffix="US$/kg" value={f.price} onChange={set("price")} />
        <BrangusSwitch id="conv-brangus" checked={brangus} onChange={setBrangus} />
        {brangus && <div className="col-span-2"><NumField id="conv-pct" label="Mejora en ganancia diaria con Brangus (heterosis)" suffix="%" value={f.pct} onChange={set("pct")} /></div>}
        <CalcButton id="conv" />
      </form>
      <div ref={ref} key={flash} className={`lg:col-span-7 space-y-6 scroll-mt-28 ${flash ? "animate-in fade-in zoom-in-95 duration-500" : ""}`} data-testid="conv-results-panel">
        {brangus && (
          <div className="flex items-center justify-between bg-[#1D3324] px-4 py-3 text-sm text-stone-100" data-testid="conv-adg-brangus">
            <span>Ganancia diaria actual {fmt(r.adgA, 2)} kg/día → con Brangus</span>
            <span className="f-display text-xl text-[#C5A059]">{fmt(r.adgB, 2)} <span className="font-sans text-xs text-stone-300">kg/día</span></span>
          </div>
        )}
        <Results r={r} brangus={brangus} />
        {brangus ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Stat accent label="Días menos a terminación" value={fmt(r.a.days - r.b.days)} unit="días" testid="conv-days-saved" />
            <Stat label="Más margen por animal" value={fmt(r.b.margin - r.a.margin)} unit="US$" testid="conv-margin-gain" />
          </div>
        ) : (
          <p className="bg-[#F3EAD7]/70 p-4 text-sm text-[#1D3324]" data-testid="conv-brangus-off">Active «Incorporar genética Brangus» para ver cuánto mejoran sus resultados.</p>
        )}
        <Note>La columna «Con Brangus» parte de su ganancia diaria actual más el % de mejora. Estimación simple: consumo diario según el peso promedio del período, sin considerar suplementos ni costos fijos. Ajuste los valores a su sistema de producción.</Note>
      </div>
    </div>
  );
};
